import {
  Application,
  Loader,
  Point,
  Sprite,
  Text,
  utils,
} from 'pixi.js';
import map from '@/assets/map';
import eventHub from '@/scripts/eventHub';
import audio from '@/scripts/audio';
import store from '@/store';
import { getLang } from '@/scripts/utils';

import BackgroundStage from './stages/BackgroundStage';
import MainStage from './stages/MainStage';
import InteractionManager from './interaction';
import Bird from './actors/Bird';
import state from './state';
import ticker from './ticker';
import Pipes from './emitters/Pipes';
import { shakeSprite } from './utils';
import Coins from './emitters/Coins';
import PowerUp from './effects/PowerUp';
import Clouds from './emitters/Clouds';
import Scarecrow from './actors/Scarecrow';
import Points from './emitters/Points';

class Game {
  constructor() {
    this.app = null;
    this.assets = map;
    this.DOMFrame = null;
    this.DOMCanvas = null;
    this.state = state;

    this.ticker = null;
    this.shareLoaded = false;

    this.backgroundStage = null;
    this.mainStage = null;
  }

  init(DOMFrame, DOMCanvas) {
    this.DOMFrame = DOMFrame;
    this.DOMCanvas = DOMCanvas;

    utils.skipHello();
    this.app = new Application({
      view: this.DOMCanvas,
      autoDensity: false,
      autoStart: false,
      transparent: true,
      // backgroundColor: 0xdbccb3,
      antialias: true,
      sharedLoader: true,
      sharedTicker: false,
    });
    this.ticker = ticker;
    this.ticker.add(() => this.app.render());

    this.resize();

    this.app.render();

    // Event management
    eventHub.$on('tutorial', () => { this.goToTutorial(); });
    eventHub.$on('play', () => { this.startPlaying(); });
    eventHub.$on('pause', () => { this.pause(); });
    eventHub.$on('resume', () => { this.resume(); });
    eventHub.$on('shake', () => { shakeSprite(this.app.stage); });
    eventHub.$on('gameover', () => { this.gameover(); });
    eventHub.$on('replay', () => { this.replay(); });
  }

  load() {
    // Load assets from map
    const sectionKeys = Object.keys(this.assets);
    sectionKeys.forEach((sectionKey) => {
      const assetKeys = Object.keys(this.assets[sectionKey]);
      assetKeys.forEach((assetKey) => {
        Loader.shared.add(`${sectionKey}/${assetKey}`, this.assets[sectionKey][assetKey].url);
      });
    });
    const assets = new Promise((resolve) => { Loader.shared.load(resolve); });
    // Init and load audio
    audio.init();
    const sound = audio.load();
    // Wait for all promises to be resolved
    return Promise.all([assets, sound]);
  }

  resume() {
    this.ticker.start();
    if (this.points.startable) this.points.start();
    state.paused = false;
  }

  pause() {
    this.ticker.stop();
    this.points.stop();
    state.paused = true;
  }

  resize() {
    const rect = this.DOMFrame.getBoundingClientRect();
    this.app.renderer.resize(rect.width, rect.height);
  }

  async setUpGame() {
    // Initialize the front stage and the background stage
    this.backgroundStage = new BackgroundStage(this.app.stage);
    this.mainStage = new MainStage(this.app.stage);
    this.cloudsGenerator = new Clouds(this.mainStage.ref);

    // Init all the actors
    this.scarecrow = new Scarecrow(this.mainStage.ref);
    this.pipesGenerator = new Pipes(this.mainStage.ref);
    this.coinsGenerator = new Coins(this.mainStage.ref);
    this.bird = new Bird(this.mainStage.ref, this.pipesGenerator, this.coinsGenerator);
    this.powerUp = new PowerUp(this.mainStage.ref, this.app.renderer, this.app.stage);

    // Init the interaction manger
    this.interaction = new InteractionManager(this.DOMFrame);
    this.points = new Points();

    this.app.stage.scale = new Point(1.03, 1.03);
    this.app.stage.position.x -= 10;
    this.app.stage.sortChildren();
  }

  goToTutorial() {
    this.bird.getReady();
  }

  async startPlaying() {
    state.reset();
    this.interaction.start();
    if (store.state.mode === 'playing') {
      this.points.start();
    }
    this.points.startable = true;

    // Perform all task needed to spawn chars and so on
    this.bird.bindFlap(this.interaction);
    this.pipesGenerator.startSpawn();
    this.coinsGenerator.startSpawn();
    this.backgroundStage.startMove();
    this.cloudsGenerator.startSpawning();
    this.bird.startFlying();
    this.scarecrow.startMoving();

    // Play the background music and other audios
    audio.play('background');
  }

  gameover() {
    audio.play('gameover');
    // First stop various binders and tickers
    this.interaction.stop();
    this.points.stop();
    this.points.startable = false;
    this.bird.gameover();
    this.coinsGenerator.gameover();
    this.pipesGenerator.gameover();
    this.backgroundStage.gameover();
  }

  replay() {
    // Reset the game state
    state.reset();

    this.backgroundStage.replay();
    this.pipesGenerator.replay();
    this.coinsGenerator.replay();
    this.bird.replay();

    this.interaction.start();
    this.points.start();
    this.points.startable = true;
  }

  async generateShareImage() {
    // Standalone function for generating share image
    const sharepath = `/assets/share/${getLang()}/share.jpg`;
    const baseImage = Sprite.from(sharepath);
    const p = new Promise((resolve) => {
      if (this.shareLoaded) resolve();
      else {
        baseImage.texture.baseTexture.on('loaded', resolve);
        this.shareLoaded = true;
      }
    });
    await p;
    const text = new Text(store.state.mode === 'gameover' ? state.score.toString() : store.state.userData.score, {
      fill: 0x666823,
      fontSize: 140,
      fontFamily: 'NibPro',
      fontWeight: 700,
    });
    text.anchor = new Point(0.5, 0.5);
    text.position.x = baseImage.width / 2;
    text.position.y = 605;
    baseImage.addChild(text);
    const b64 = this.app.renderer.plugins.extract.base64(baseImage, 'image/jpg', 0.6);
    return b64;
  }
}

export default Game;
